import React, { useState, useMemo } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { UI } from "@newageerp/nae-react-ui";
import { TabContainer } from "@newageerp/ui.tabs.tab-container";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import DateField from "./fields/DateField";
import { COUNTRIES } from '../Utils/countries';
import { toast } from "react-toastify";
import ApiRest from "../../service/ApiRest";
import { IconType } from '@newageerp/ui.icons.base.icon';
import { IconList } from '@newageerp/ui.icons.base.icon-list';
import { TwBackgroundColors, TwTextColors } from '@newageerp/ui.styles.tailwind';
import { SquareButton } from "@newageerp/ui.buttons.base.square-button";
import { Popup, PopupSize } from "@newageerp/ui.popups.base.popup";
import PhoneNumberPopup from './PhoneNumberPopup';
import AddressPopup from './AddressPopup';
import CommentHistoryPopup from './CommentHistoryPopup';


export interface PhoneNumber {
    id: number;
    number: string;
    country: string;
    type: string;
    comment: string;
}

export interface Address {
    id: number;
    country: string;
    city: string;
    address: string;
    type: string;
    comment?: string;
}

export interface Candidate {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    birthDate: string;
    citizenship: string;
    position: string;
    applicationDate: string;
    reviewDate: string;
    status: 'review' | 'accepted' | 'rejected';
    email: string;
    personalId?: string;
    phoneNumbers: PhoneNumber[];
    addresses: Address[];
    comment: string;
    history: Array<{
        id: number;
        date: string;
        change: string;
    }>;
    employee?: {
        id: number;
    } | null;
}

interface Props {
    element: Candidate;
    saveData: (data: Partial<Candidate>) => void;
}

const POSITIONS = [
    { id: 'Vairuotojas', name: 'Vairuotojas' }
] as const;

const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '2px solid #dee2e6',
    marginBottom: '1rem'
};

export default function CandidateForm(props: Props) {
    const { element, saveData } = props;
    const { t } = useTranslation();
    const history = useHistory();

    const [firstName, setFirstName] = useState(element.firstName || "");
    const [lastName, setLastName] = useState(element.lastName || "");
    const [birthDate, setBirthDate] = useState(element.birthDate || "");
    const [citizenship, setCitizenship] = useState(element.citizenship || "");
    const [position, setPosition] = useState(element.position || POSITIONS[0].id);
    const [applicationDate, setApplicationDate] = useState(element.applicationDate || "");
    const [reviewDate, setReviewDate] = useState(element.reviewDate || "");
    const [status, setStatus] = useState(element.status || "review");
    const [email, setEmail] = useState(element.email || "");
    const [personalId, setPersonalId] = useState(element.personalId || "");
    const [comment, setComment] = useState(element.comment || "");
    const [commentHistory, setCommentHistory] = useState(element.history || []);

    const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>((element.phoneNumbers || []).map(phone => ({
        ...phone,
        comment: phone.comment || ''
    })));
    const [addresses, setAddresses] = useState<Address[]>(element.addresses || []);
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<number[]>([]);
    const [selectedAddresses, setSelectedAddresses] = useState<number[]>([]);
    const [showPhoneNumberPopup, setShowPhoneNumberPopup] = useState(false);
    const [showAddressPopup, setShowAddressPopup] = useState(false);
    const [editPhoneNumber, setEditPhoneNumber] = useState<PhoneNumber | null>(null);
    const [editAddress, setEditAddress] = useState<Address | null>(null);
    const [isAlreadyConverted, setIsAlreadyConverted] = useState(
        status === 'accepted' && !!element.employee?.id
    );

    const [showHistoryPopup, setShowHistoryPopup] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [savedValues, setSavedValues] = useState({
        firstName: element.firstName || "",
        lastName: element.lastName || "",
        personalId: element.personalId || ""
    });

    const handlePhoneNumberSave = (phoneNumber: PhoneNumber) => {
        const formattedPhoneNumber = {
            ...phoneNumber,
            comment: phoneNumber.comment || '',
            id: phoneNumber.id || Date.now()
        };

        if (editPhoneNumber) {
            setPhoneNumbers(prev => prev.map(p => p.id === editPhoneNumber.id ? formattedPhoneNumber : p));
        } else {
            setPhoneNumbers(prev => [...prev, formattedPhoneNumber]);
        }
        setShowPhoneNumberPopup(false);
        setEditPhoneNumber(null);
    };

    const handleAddressSave = (address: Address) => {
        if (editAddress) {
            setAddresses(prev => prev.map(a => a.id === editAddress.id ? address : a));
        } else {
            setAddresses(prev => [...prev, { ...address, id: Date.now() }]);
        }
        setShowAddressPopup(false);
        setEditAddress(null);
    };

    const handleSave = async () => {
        const formattedData: Partial<Candidate> = {
            firstName,
            lastName,
            fullName: `${firstName} ${lastName}`.trim(),
            birthDate,
            citizenship,
            position,
            applicationDate,
            reviewDate,
            status,
            email,
            personalId,
            phoneNumbers,
            addresses,
        };

        if (comment.trim() !== "") {
            const newHistoryItem = {
                id: Date.now(),
                date: new Date().toISOString(),
                change: comment
            };
            formattedData.history = [newHistoryItem];
        }

        await saveData(formattedData);

        setSavedValues({
            firstName,
            lastName,
            personalId
        });

        setComment("");
        setCommentHistory(prev => [...prev, ...(formattedData.history || [])]);
    };

    const handleConvertToEmployee = async () => {
        try {
            const errors = getEmployeeCreationErrors();
            if (errors.length > 0) {
                toast.error(`Trūksta duomenų: ${errors.join(', ')}`);
                return;
            }

            const employeeData = {
                firstName,
                lastName,
                birthDate,
                country: citizenship,
                position,
                personalCode: personalId,
                hireDate: new Date().toISOString().split('T')[0],
                email,
                candidateId: element.id,
                phoneNumbers: phoneNumbers.map(phone => ({
                    ...phone,
                    type: phone.type,
                    country: phone.country,
                    number: phone.number,
                    comment: phone.comment || ''
                })),
                addresses: addresses.map(addr => ({
                    ...addr,
                    type: addr.type,
                    country: addr.country,
                    city: addr.city,
                    address: addr.address,
                    comment: addr.comment || ''
                }))
            };

            const response = await ApiRest.convertCandidateToEmployee(employeeData);

            if (response) {
                const updateData: Partial<Candidate> = {
                    status: 'accepted',
                    employee: { id: response.id }
                };
                
                if (!reviewDate) {
                    updateData.reviewDate = new Date().toISOString().split('T')[0];
                }

                saveData(updateData);

                toast.success('Kandidatas sėkmingai konvertuotas į darbuotoją');
                history.push('/employee/' + response.id);
            }
        } catch (error: any) {
            if (error.includes('already exists')) {
                toast.error('Darbuotojas su šiuo asmens kodu jau egzistuoja');
            } else {
                toast.error('Klaida konvertuojant kandidatą į darbuotoją');
            }
            console.error('Error converting candidate to employee:', error);
        }
    };

    const getEmployeeCreationErrors = () => {
        const errors: string[] = [];
        if (isAlreadyConverted) errors.push('Kandidatas jau konvertuotas į darbuotoją');
        if (!savedValues.firstName.trim()) errors.push('Vardas');
        if (!savedValues.lastName.trim()) errors.push('Pavardė');
        if (!savedValues.personalId?.trim()) errors.push('Asmens kodas');
        return errors;
    };

    const handleDelete = async () => {
        try {
            await ApiRest.deleteElement('candidate', element.id);
            toast.success('Kandidatas sėkmingai ištrintas');
            history.push('/candidates');
        } catch (error: any) {
            toast.error('Klaida trinant kandidatą');
            console.error('Error deleting candidate:', error);
        }
        setShowDeleteConfirm(false);
    };

    const ContactTab = useMemo(() => (
        <div>
            {/* Phone Numbers Section */}
            <Row className="mt-4">
                <Col>
                    <div style={headerStyle}>
                        <h4 style={{ margin: 0, flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            TEL. NUMERIS
                            <SquareButton
                                bgColor={TwBackgroundColors.slate50}
                                icon={{
                                    type: IconType.SolidButton,
                                    icon: IconList.plus,
                                    className: TwTextColors.slate400,
                                }}
                                onClick={() => {
                                    setEditPhoneNumber(null);
                                    setShowPhoneNumberPopup(true);
                                }}
                            />
                            {selectedPhoneNumbers.length > 0 && (
                                <SquareButton
                                    bgColor={TwBackgroundColors.slate50}
                                    icon={{
                                        type: IconType.SolidButton,
                                        icon: IconList.trash,
                                        className: TwTextColors.slate400,
                                    }}
                                    onClick={() => {
                                        setPhoneNumbers(prev => prev.filter(p => !selectedPhoneNumbers.includes(p.id)));
                                        setSelectedPhoneNumbers([]);
                                    }}
                                />
                            )}
                        </h4>
                    </div>
                </Col>
            </Row>

            <Table striped bordered hover className="mb-4">
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}>
                            <Form.Check
                                type="checkbox"
                                checked={phoneNumbers.length > 0 && selectedPhoneNumbers.length === phoneNumbers.length}
                                onChange={() => {
                                    if (selectedPhoneNumbers.length === phoneNumbers.length) {
                                        setSelectedPhoneNumbers([]);
                                    } else {
                                        setSelectedPhoneNumbers(phoneNumbers.map(p => p.id));
                                    }
                                }}
                            />
                        </th>
                        <th>Tel. numeris</th>
                        <th>Šalis</th>
                        <th>Tipas</th>
                        <th>Pastaba</th>
                        <th style={{ width: '100px' }}>Veiksmai</th>
                    </tr>
                </thead>
                <tbody>
                    {phoneNumbers.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="text-center py-4 text-muted">
                                Nėra pridėtų tel. numerių
                            </td>
                        </tr>
                    ) : (
                        phoneNumbers.map(phone => (
                            <tr key={phone.id}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectedPhoneNumbers.includes(phone.id)}
                                        onChange={() => {
                                            setSelectedPhoneNumbers(prev =>
                                                prev.includes(phone.id)
                                                    ? prev.filter(id => id !== phone.id)
                                                    : [...prev, phone.id]
                                            );
                                        }}
                                    />
                                </td>
                                <td>{phone.number}</td>
                                <td>{phone.country}</td>
                                <td>{phone.type}</td>
                                <td>{phone.comment}</td>
                                <td>
                                    <SquareButton
                                        bgColor={TwBackgroundColors.slate50}
                                        icon={{
                                            type: IconType.SolidButton,
                                            icon: IconList.editPencil,
                                            className: TwTextColors.slate400,
                                        }}
                                        onClick={() => {
                                            setEditPhoneNumber(phone);
                                            setShowPhoneNumberPopup(true);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {/* Email Section */}
            <Row className="mt-4">
                <Col>
                    <div style={headerStyle}>
                        <h4 style={{ margin: 0 }}>EL. PAŠTAS</h4>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <Form.Group>
                        <Form.Control
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            {/* Address Section */}
            <Row className="mt-4">
                <Col>
                    <div style={headerStyle}>
                        <h4 style={{ margin: 0, flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            ADRESAS
                            <SquareButton
                                bgColor={TwBackgroundColors.slate50}
                                icon={{
                                    type: IconType.SolidButton,
                                    icon: IconList.plus,
                                    className: TwTextColors.slate400,
                                }}
                                onClick={() => {
                                    setEditAddress(null);
                                    setShowAddressPopup(true);
                                }}
                            />
                            {selectedAddresses.length > 0 && (
                                <SquareButton
                                    bgColor={TwBackgroundColors.slate50}
                                    icon={{
                                        type: IconType.SolidButton,
                                        icon: IconList.trash,
                                        className: TwTextColors.slate400,
                                    }}
                                    onClick={() => {
                                        setAddresses(prev => prev.filter(a => !selectedAddresses.includes(a.id)));
                                        setSelectedAddresses([]);
                                    }}
                                />
                            )}
                        </h4>
                    </div>
                </Col>
            </Row>

            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}>
                            <Form.Check
                                type="checkbox"
                                checked={addresses.length > 0 && selectedAddresses.length === addresses.length}
                                onChange={() => {
                                    if (selectedAddresses.length === addresses.length) {
                                        setSelectedAddresses([]);
                                    } else {
                                        setSelectedAddresses(addresses.map(a => a.id));
                                    }
                                }}
                            />
                        </th>
                        <th>Šalis</th>
                        <th>Miestas</th>
                        <th>Gatvė, namo numeris</th>
                        <th>Tipas</th>
                        <th>Pastaba</th>
                        <th style={{ width: '100px' }}>Veiksmai</th>
                    </tr>
                </thead>
                <tbody>
                    {addresses.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="text-center py-4 text-muted">
                                Nėra pridėtų adresų
                            </td>
                        </tr>
                    ) : (
                        addresses.map(address => (
                            <tr key={address.id}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectedAddresses.includes(address.id)}
                                        onChange={() => {
                                            setSelectedAddresses(prev =>
                                                prev.includes(address.id)
                                                    ? prev.filter(id => id !== address.id)
                                                    : [...prev, address.id]
                                            );
                                        }}
                                    />
                                </td>
                                <td>{address.country}</td>
                                <td>{address.city}</td>
                                <td>{address.address}</td>
                                <td>{address.type}</td>
                                <td>{address.comment}</td>
                                <td>
                                    <SquareButton
                                        bgColor={TwBackgroundColors.slate50}
                                        icon={{
                                            type: IconType.SolidButton,
                                            icon: IconList.editPencil,
                                            className: TwTextColors.slate400,
                                        }}
                                        onClick={() => {
                                            setEditAddress(address);
                                            setShowAddressPopup(true);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {/* Popups */}
            {showPhoneNumberPopup && (
                <Popup
                    onClose={() => {
                        setShowPhoneNumberPopup(false);
                        setEditPhoneNumber(null);
                    }}
                    header={editPhoneNumber ? "Redaguoti tel. numerį" : "Pridėti tel. numerį"}
                    size={PopupSize.w500}
                >
                    <PhoneNumberPopup
                        driverId={element.id}
                        phoneNumber={editPhoneNumber || undefined}
                        onClose={() => {
                            setShowPhoneNumberPopup(false);
                            setEditPhoneNumber(null);
                        }}
                        onSave={handlePhoneNumberSave}
                    />
                </Popup>
            )}

            {showAddressPopup && (
                <Popup
                    onClose={() => {
                        setShowAddressPopup(false);
                        setEditAddress(null);
                    }}
                    header={editAddress ? "Redaguoti adresą" : "Pridėti adresą"}
                    size={PopupSize.w500}
                >
                    <AddressPopup
                        driverId={element.id}
                        address={editAddress || undefined}
                        onClose={() => {
                            setShowAddressPopup(false);
                            setEditAddress(null);
                        }}
                        onSave={handleAddressSave}
                    />
                </Popup>
            )}
        </div>
    ), [email, phoneNumbers, addresses, selectedPhoneNumbers, selectedAddresses, showPhoneNumberPopup, showAddressPopup, editPhoneNumber, editAddress]);

    const GeneralTab = useMemo(() => (
        <Row>
            <Col md={6}>
                <Form>
                    <Form.Group>
                        <Form.Label>Vardas</Form.Label>
                        <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Gimimo data</Form.Label>
                        <DateField value={birthDate} setValue={setBirthDate} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Asmens kodas</Form.Label>
                        <Form.Control value={personalId} onChange={(e) => setPersonalId(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Pareigos</Form.Label>
                        <Form.Control
                            as="select"
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                        >
                            {POSITIONS.map((pos) => (
                                <option key={pos.id} value={pos.id}>
                                    {pos.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Paraiškos data</Form.Label>
                        <DateField value={applicationDate} setValue={setApplicationDate} />
                    </Form.Group>
                </Form>
            </Col>
            <Col md={6}>
                <Form>
                    <Form.Group>
                        <Form.Label>Pavardė</Form.Label>
                        <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Pilietybė</Form.Label>
                        <Form.Control
                            as="select"
                            value={citizenship}
                            onChange={(e) => setCitizenship(e.target.value)}
                        >
                            {COUNTRIES.map((prefix) => (
                                <option key={prefix.country} value={prefix.name}>
                                    {prefix.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Statusas</Form.Label>
                        <Form.Control
                            as="select"
                            value={status}
                            onChange={(e) => setStatus(e.target.value as Candidate['status'])}
                        >
                            <option value="review">Vertinamas</option>
                            <option value="accepted">Priimtas</option>
                            <option value="rejected">Atmestas</option>
                        </Form.Control>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Vertinimo data</Form.Label>
                        <DateField value={reviewDate} setValue={setReviewDate} />
                    </Form.Group>
                </Form>
            </Col>
        </Row>
    ), [firstName, lastName, birthDate, citizenship, position, status, applicationDate, reviewDate, personalId]);

    const items = useMemo(() => [
        { content: GeneralTab, tab: { children: t("Bendra") } },
        { content: ContactTab, tab: { children: t("Kontaktai") } },
    ], [GeneralTab, ContactTab, t]);

    return (
        <Row>
            <Col md={7} lg={8}>
                <UI.Card.WhiteCard>
                    <UI.MVC.MvcViewTitle
                        onBack={() => history.goBack()}
                        title={`Kandidatai - ${firstName} ${lastName}`.trim() || "Kandidatai"}
                    />
                    <TabContainer items={items} />
                    <Card.Footer>
                        <Row className="d-flex align-items-center">
                            <Col>
                                <Button
                                    className="mb-2"
                                    type="button"
                                    variant="danger"
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    Ištrinti kandidatą
                                </Button>
                            </Col>
                            <Col className="text-right">
                                <Button
                                    className="mb-2 me-2"
                                    type="button"
                                    variant={status !== 'review' || getEmployeeCreationErrors().length > 0 ? "secondary" : "success"}
                                    disabled={status !== 'review' || getEmployeeCreationErrors().length > 0}
                                    title={
                                        status !== 'review'
                                            ? 'Kandidatas turi būti "Vertinamas"'
                                            : getEmployeeCreationErrors().length > 0
                                                ? `Trūksta duomenų: ${getEmployeeCreationErrors().join(', ')}`
                                                : ''
                                    }
                                    onClick={handleConvertToEmployee}
                                >
                                    Sukurti darbuotoją
                                </Button>
                                <Button
                                    className="mb-2"
                                    type="button"
                                    variant="primary"
                                    onClick={handleSave}
                                >
                                    Išsaugoti
                                </Button>
                            </Col>
                        </Row>
                    </Card.Footer>
                </UI.Card.WhiteCard>
            </Col>
            <Col md={5} lg={4}>
                <Card>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Komentaras</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                style={{ minHeight: '80px' }}
                            />
                        </Form.Group>
                        <div className="flex justify-between items-center mb-2">
                            <h5 className="mb-0">Komentarų istorija</h5>
                            <Button
                                variant="outline-light"
                                size="sm"
                                onClick={() => setShowHistoryPopup(true)}
                            >
                                <i className="fa-regular fa-expand mr-2"></i>
                                Plačiau
                            </Button>
                        </div>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th style={{ width: "160px" }}>DATA</th>
                                    <th>PASTABOS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...commentHistory]
                                    .reverse()
                                    .map((el) => (
                                        <tr key={`hist-${el.id}`}>
                                            <td className="whitespace-nowrap">{moment(el.date).format('YYYY-MM-DD HH:mm')}</td>
                                            <td style={{
                                                maxWidth: '250px',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'normal',
                                                whiteSpace: 'normal',
                                                hyphens: 'auto'
                                            }}>
                                                {el.change}
                                            </td>
                                        </tr>
                                    ))}
                                {commentHistory.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="text-center py-4 text-muted">
                                            Nėra komentarų istorijos
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </Card.Body>
                </Card>
            </Col>

            {/* History Popup */}
            {showHistoryPopup && (
                <Popup
                    onClose={() => setShowHistoryPopup(false)}
                    header="Komentarų istorija"
                    size={PopupSize.w75p}
                >
                    <CommentHistoryPopup
                        history={commentHistory}
                    />
                </Popup>
            )}

            {showDeleteConfirm && (
                <Popup
                    onClose={() => setShowDeleteConfirm(false)}
                    header="Patvirtinti trynimą"
                    size={PopupSize.w500}
                >
                    <div className="p-2">
                        <p className="">
                            Ar tikrai norite ištrinti kandidatą <strong>{firstName} {lastName}</strong>?
                        </p>
                        <p className="text-muted mb-12">
                            Visi kandidato duomenys bus prarasti.
                        </p>
                        <div className="d-flex justify-content-end">
                            <Button
                                variant="secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                                style={{ marginRight: '0.5rem' }}
                            >
                                Atšaukti
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                            >
                                Ištrinti
                            </Button>
                        </div>
                    </div>
                </Popup>
            )}
        </Row>
    );
}